import React, { useEffect, useState } from 'react';

export interface NotificationProps {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message?: React.ReactNode;
  duration?: number;
  onClose: (id: string) => void;
}

const Notification: React.FC<NotificationProps> = ({
  id,
  type,
  title,
  message,
  duration = 4000,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // 延迟显示动画
    const showTimer = setTimeout(() => setIsVisible(true), 10);

    // 自动关闭
    const hideTimer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [duration]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose(id);
    }, 200);
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <div className="flex size-10 items-center justify-center rounded-full border border-emerald-100 bg-emerald-50">
            <svg className="size-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="flex size-10 items-center justify-center rounded-full border border-rose-100 bg-rose-50">
            <svg className="size-5 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        );
      case 'warning':
        return (
          <div className="flex size-10 items-center justify-center rounded-full border border-amber-100 bg-amber-50">
            <svg className="size-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
        );
      case 'info':
      default:
        return (
          <div className="flex size-10 items-center justify-center rounded-full border border-blue-100 bg-blue-50">
            <svg className="size-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  return (
    <div
      className={`
        w-full flex justify-center pointer-events-none
        transition-transform transition-opacity duration-200 ease-out motion-reduce:transition-none
        data-[state=hidden]:-translate-y-6 data-[state=hidden]:opacity-0 data-[state=hidden]:scale-95
        data-[state=visible]:translate-y-0 data-[state=visible]:opacity-100 data-[state=visible]:scale-100
      `}
      data-state={isVisible && !isLeaving ? 'visible' : 'hidden'}
    >
      <div
        onClick={handleClose}
        className={`
            pointer-events-auto
            flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3
            shadow-lg cursor-pointer transition-transform duration-150 ease-out active:scale-95
            motion-reduce:transition-none
        `}
      >
        {getIcon()}

        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <h4 className="text-base font-semibold text-slate-900 leading-snug text-balance">
            {title}
          </h4>
          {message && (
            <p className="mt-1 text-sm text-slate-600 leading-snug text-pretty tabular-nums break-words">
              {message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notification; 
